import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import { getOpenRouterLogs } from '../../utils/APIs';
import DateRangePicker from '../UI/DateRangePicker';
import Loader from '../UI/Loader/Loader';
import AgGridWrapper from '../UI/AgGrid';
import { Select } from '../UI';
import AIUsageChart from './AIUsageChart';
import { aggregateLogUsage } from './aiUsageChartData';

const AILogsModal = ({ isOpen, onClose }) => {
    const { userData } = useUserContext();

    const [logs, setLogs] = useState([]);
    const [showChart, setShowChart] = useState(false);
    const [showTable, setShowTable] = useState(true);
    const [dailyUsage, setDailyUsage] = useState([]);
    const [chartUsesPageData, setChartUsesPageData] = useState(false);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total_records: 0,
        total_pages: 0,
    });

    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const handleRefresh = useCallback(() => {
        setRefreshTrigger((prev) => prev + 1);
    }, []);

    useEffect(() => {
        let isMounted = true;

        if (isOpen && userData?.token && userData?.selectedOrganization?.org_token) {
            const loadData = async () => {
                setLoading(true);
                try {
                    const params = {
                        start_date: startDate,
                        end_date: endDate,
                        page,
                        limit,
                    };

                    const res = await getOpenRouterLogs(
                        userData.token,
                        userData.selectedOrganization.org_token,
                        params
                    );

                    if (!isMounted) return;

                    if (res?.status && res?.data) {
                        const { logs: logList = [], summary: logSummary = null, pagination: pageInfo = {} } = res.data;
                        setLogs(logList);
                        const hasDailyUsage = Array.isArray(res.data.daily_usage) && res.data.daily_usage.length > 0;
                        setDailyUsage(hasDailyUsage ? res.data.daily_usage : aggregateLogUsage(logList));
                        setChartUsesPageData(!hasDailyUsage && logList.length > 0);
                        setSummary(logSummary);
                        setPagination({
                            page: pageInfo.page || page,
                            limit: pageInfo.limit || limit,
                            total_records: pageInfo.total_records || 0,
                            total_pages: pageInfo.total_pages || 0,
                        });
                    } else {
                        setLogs([]);
                        setDailyUsage([]);
                        setSummary(null);
                    }
                } catch (error) {
                    if (!isMounted) return;
                    console.error('Failed to fetch AI usage logs:', error);
                    const msg = error?.response?.data?.message || error?.message || 'Failed to load AI usage logs.';
                    toast.error(msg);
                    setLogs([]);
                    setDailyUsage([]);
                    setSummary(null);
                } finally {
                    if (isMounted) {
                        setLoading(false);
                    }
                }
            };

            loadData();
        }

        return () => {
            isMounted = false;
        };
    }, [isOpen, userData, startDate, endDate, page, limit, refreshTrigger]);

    // Handle ESC key press
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleDateChange = (start, end) => {
        const diff = dayjs(end).diff(dayjs(start), 'day');
        if (diff > 30) {
            toast.warn('AI logs queries are limited to a maximum of 30 days.');
            const clampedStart = dayjs(end).subtract(30, 'day').format('YYYY-MM-DD');
            setStartDate(clampedStart);
            setEndDate(end);
        } else {
            setStartDate(start);
            setEndDate(end);
        }
        setPage(1); // Reset to first page on filter change
    };

    const handleLimitChange = (e) => {
        setLimit(Number(e.target.value));
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.total_pages) {
            setPage(newPage);
        }
    };

    const formatDateDisplay = (dateVal) => {
        if (!dateVal) return '—';
        const str = typeof dateVal === 'string' ? dateVal.trim() : '';

        // If it's a date-only string (e.g. "YYYY-MM-DD") or has a zeroed 00:00 time, format only as date
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(str);
        const isZeroTime = /T00:00:00(\.000)?Z?$/i.test(str) || /\s00:00:00$/i.test(str);

        if (isDateOnly || isZeroTime) {
            return dayjs(dateVal).format('MMM D, YYYY');
        }

        if (typeof dateVal === 'number') {
            return dateVal < 1e11
                ? dayjs.unix(dateVal).format('MMM D, YYYY HH:mm')
                : dayjs(dateVal).format('MMM D, YYYY HH:mm');
        }
        return dayjs(dateVal).format('MMM D, YYYY HH:mm');
    };

    const formatCost = (val) => {
        if (val === null || val === undefined || isNaN(val)) return '$0.00';
        const num = Number(val);
        return num < 0.01 && num > 0 ? `$${num.toFixed(6)}` : `$${num.toFixed(4)}`;
    };

    const columnDefs = [
        {
            headerName: 'Date',
            field: 'created_at',
            minWidth: 180,
            filter: 'agDateColumnFilter',
            valueGetter: ({ data }) => {
                const value = data?.created_at;
                if (!value) return null;
                const date = typeof value === 'number' && value < 1e11
                    ? dayjs.unix(value) : dayjs(value);
                return date.isValid() ? date.toDate() : null;
            },
            valueFormatter: ({ data }) => formatDateDisplay(data?.created_at),
        },
        { headerName: 'User', field: 'user', minWidth: 220, tooltipField: 'user', valueFormatter: ({ value }) => value || '—' },
        { headerName: 'Model', field: 'model', minWidth: 200, valueFormatter: ({ value }) => value || 'Unknown' },
        ...[
            ['Prompt Tokens', 'prompt'],
            ['Completion Tokens', 'completion'],
            ['Total Tokens', 'total'],
        ].map(([headerName, tokenType]) => ({
            headerName,
            colId: `tokens.${tokenType}`,
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            valueGetter: ({ data }) => Number(data?.tokens?.[tokenType]
                ?? (tokenType === 'total' ? Number(data?.tokens?.prompt || 0) + Number(data?.tokens?.completion || 0) : 0)),
            valueFormatter: ({ value }) => (value || 0).toLocaleString(),
        })),
        {
            headerName: 'Cost',
            field: 'cost',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            valueFormatter: ({ value }) => formatCost(value),
        },
        {
            headerName: 'Status',
            field: 'status',
            valueGetter: ({ data }) => (data?.status || 'success').toLowerCase(),
            cellRenderer: ({ value }) => (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                    value === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                    {value}
                </span>
            ),
        },
    ];

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-logs-modal-title"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col relative overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 shrink-0 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h2 id="ai-logs-modal-title" className="text-xl font-bold text-gray-900">
                                AI Usage Logs
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Detailed record of AI completions, token usage, and costs for your organization.
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 rounded-lg p-1.5 hover:bg-gray-200 transition-colors"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                    {/* Summary & Filters */}
                    <div className="px-5 py-3 border-b border-gray-100 bg-white flex flex-wrap items-center gap-3 relative z-20">
                        {/* Summary Cards */}
                        {summary && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <div className="bg-indigo-50/70 border border-indigo-100 rounded-lg px-3 py-2 min-w-[120px]">
                                    <p className="text-[10px] font-medium text-indigo-600 uppercase tracking-wide">Total Requests</p>
                                    <p className="text-lg font-bold text-gray-900 mt-1">
                                        {(summary.total_requests || 0).toLocaleString()}
                                    </p>
                                </div>
                                <div className="bg-blue-50/70 border border-blue-100 rounded-lg px-3 py-2 min-w-[120px]">
                                    <p className="text-[10px] font-medium text-blue-600 uppercase tracking-wide">Total Tokens</p>
                                    <p className="text-lg font-bold text-gray-900 mt-1">
                                        {(summary.total_tokens || 0).toLocaleString()}
                                    </p>
                                    <p className="text-[10px] text-gray-500 mt-0.5">
                                        {(summary.total_prompt_tokens || 0).toLocaleString()} in / {(summary.total_completion_tokens || 0).toLocaleString()} out
                                    </p>
                                </div>
                                <div className="bg-emerald-50/70 border border-emerald-100 rounded-lg px-3 py-2 min-w-[120px]">
                                    <p className="text-[10px] font-medium text-emerald-600 uppercase tracking-wide">Total Cost</p>
                                    <p className="text-lg font-bold text-gray-900 mt-1">
                                        {formatCost(summary.total_cost)}
                                    </p>
                                    {summary.margin_percent > 0 && (
                                        <p className="text-[10px] text-gray-500 mt-0.5">
                                            Includes {summary.margin_percent}% plan margin
                                        </p>
                                    )}
                                </div>
                                {summary.total_provider_cost !== undefined && (
                                    <div className="bg-amber-50/70 border border-amber-100 rounded-lg px-3 py-2 min-w-[120px]">
                                        <p className="text-[10px] font-medium text-amber-700 uppercase tracking-wide">Provider Raw Cost</p>
                                        <p className="text-lg font-bold text-gray-900 mt-1">
                                            {formatCost(summary.total_provider_cost)}
                                        </p>
                                        <p className="text-[10px] text-amber-600 mt-0.5">Platform Admin Only</p>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="ml-auto flex shrink-0 items-end gap-2">
                                <DateRangePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    onDateChange={handleDateChange}
                                    label="Date Range"
                                    allowFuture={false}
                                    align="right"
                                    maxDays={30}
                                />
                                <button
                                    type="button"
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    className="p-2.5 mt-auto text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center transition-colors disabled:opacity-50"
                                    title="Refresh logs"
                                    aria-label="Refresh logs"
                                >
                                    <svg
                                        className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                        />
                                    </svg>
                                </button>
                        </div>
                    </div>

                    {/* Table Content Area */}
                    <div className="relative px-5 pb-4">
                        <h3 className="mt-3 mb-2">
                            <button
                                type="button"
                                aria-expanded={showChart}
                                aria-controls="ai-usage-chart-panel"
                                onClick={() => setShowChart((visible) => !visible)}
                                className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                            >
                                Usage chart
                                <ChevronDown aria-hidden="true" className={`mr-2 h-4 w-4 shrink-0 text-gray-500 transition-transform ${showChart ? '' : '-rotate-90'}`} />
                            </button>
                        </h3>
                        <div id="ai-usage-chart-panel" hidden={!showChart}>
                            {showChart && (loading ? <Loader message="Loading chart..." /> : (
                                <AIUsageChart data={dailyUsage} startDate={startDate} endDate={endDate} pageOnly={chartUsesPageData} hasLogs={logs.length > 0} />
                            ))}
                        </div>
                        <h3 className="mt-3 mb-2">
                            <button
                                type="button"
                                aria-expanded={showTable}
                                aria-controls="ai-usage-table-panel"
                                onClick={() => setShowTable((visible) => !visible)}
                                className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                            >
                                Usage logs
                                <ChevronDown aria-hidden="true" className={`mr-2 h-4 w-4 shrink-0 text-gray-500 transition-transform ${showTable ? '' : '-rotate-90'}`} />
                            </button>
                        </h3>
                        <div id="ai-usage-table-panel" hidden={!showTable}>
                            {loading ? (
                                <div className="flex items-center justify-center h-full min-h-[300px]">
                                    <Loader message="Loading logs..." />
                                </div>
                            ) : logs.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-6">
                                    <div className="w-14 h-14 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mb-3">
                                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={1.5}
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                        </svg>
                                    </div>
                                    <h3 className="text-base font-semibold text-gray-800">No AI usage logs found</h3>
                                    <p className="text-sm text-gray-500 max-w-sm mt-1">
                                        No completions were recorded in the selected date range. Try broadening your date filter.
                                    </p>
                                </div>
                            ) : (
                                <AgGridWrapper
                                    tableId="ai-usage-logs-table"
                                    rowData={logs}
                                    columnDefs={columnDefs}
                                    defaultColDef={{ sortable: true, filter: true, resizable: true, minWidth: 120, flex: 1, wrapHeaderText: true, autoHeaderHeight: true }}
                                    pagination={false}
                                />
                            )}
                        </div>
                    </div>

                </div>

                {/* Pagination Footer */}
                {showTable && <div className="px-6 py-3 shrink-0 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-xs text-gray-600">
                            {/* Rows per page selector */}
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 font-medium">Rows:</span>
                                <Select
                                    size="sm"
                                    aria-label="Rows per page"
                                    value={limit}
                                    onChange={handleLimitChange}
                                    options={[
                                        { value: 10, label: '10' },
                                        { value: 20, label: '20' },
                                        { value: 50, label: '50' },
                                        { value: 100, label: '100' },
                                    ]}
                                    className="w-20"
                                />
                            </div>
                    <div>
                        {pagination.total_records > 0 ? (
                            <span>
                                Showing{' '}
                                <strong className="font-semibold text-gray-900">
                                    {(pagination.page - 1) * pagination.limit + 1}
                                </strong>{' '}
                                to{' '}
                                <strong className="font-semibold text-gray-900">
                                    {Math.min(pagination.page * pagination.limit, pagination.total_records)}
                                </strong>{' '}
                                of{' '}
                                <strong className="font-semibold text-gray-900">
                                    {pagination.total_records.toLocaleString()}
                                </strong>{' '}
                                entries
                            </span>
                        ) : (
                            <span>0 entries</span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page <= 1 || loading}
                            className="px-3 py-1.5 border border-gray-300 rounded-md bg-white font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Previous
                        </button>

                        <span className="px-2 text-gray-700 font-medium">
                            Page {pagination.page} of {Math.max(1, pagination.total_pages)}
                        </span>

                        <button
                            type="button"
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page >= pagination.total_pages || loading}
                            className="px-3 py-1.5 border border-gray-300 rounded-md bg-white font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Next
                        </button>
                    </div>
                </div>}
            </div>
        </div>,
        document.body
    );
};

export default AILogsModal;

